// Shuffle the top-level steps of a voice's mini-pattern, preserving
// density (same set of atoms + rests, different order).
//
// MiniPattern is a flat MiniNode[] at the top level; we permute it
// in place via a seeded Fisher-Yates so the result is reproducible
// per (song, seed). Voices whose pattern is `kind: 'raw'` (parser
// couldn't structure them) or whose top-level has fewer than 2 items
// (nothing to shuffle) are filtered out at the palette layer via the
// returned error.

import type { EditRecipe } from '../blocks/editContext';
import type { MiniPattern } from '../blocks/types';
import { mulberry32, rngShuffle } from './rng';
import type { Transform } from './types';

export const shuffleTransform: Transform = {
  id: 'shuffle',
  category: 'rhythm',
  verb: 'Shuffle steps',
  description: 'Permute the top-level steps; density preserved.',
  apply({ song, voiceName, seed }) {
    const stmt = song.statements.find(
      (s) => s.kind === 'const' && s.name === voiceName,
    );
    if (!stmt || stmt.kind !== 'const') {
      return { ok: false, reason: `no const named "${voiceName}"` };
    }
    const expr = stmt.expr;
    if (expr.kind !== 'voice.s' && expr.kind !== 'voice.note') {
      return {
        ok: false,
        reason: `"${voiceName}" is not a voice (kind: ${expr.kind})`,
      };
    }
    if (expr.pattern.kind !== 'mini') {
      return {
        ok: false,
        reason: `"${voiceName}" pattern is unstructured — cannot shuffle`,
      };
    }
    if (expr.pattern.nodes.length < 2) {
      return {
        ok: false,
        reason: `"${voiceName}" has fewer than 2 steps — nothing to shuffle`,
      };
    }
    const rng = mulberry32(seed);
    // Shuffle a fresh copy so the apply function stays pure; the
    // recipe writes the shuffled copy onto the draft.
    const shuffled: MiniPattern = rngShuffle(rng, expr.pattern.nodes.slice());
    const recipe: EditRecipe = (draft) => {
      const target = draft.statements.find(
        (s) => s.kind === 'const' && s.name === voiceName,
      );
      if (!target || target.kind !== 'const') return;
      const ex = target.expr;
      if (ex.kind !== 'voice.s' && ex.kind !== 'voice.note') return;
      if (ex.pattern.kind !== 'mini') return;
      ex.pattern = { kind: 'mini', nodes: shuffled };
    };
    return {
      ok: true,
      recipe,
      summary: `shuffle ${voiceName}`,
    };
  },
};
